import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ActionMenuItemComponent } from '../../../../../shared/components/action-menu-item/action-menu-item.component';
import { ActionMenuItem } from '../../../../../shared/models/action-menu-item.class';
import { ActivatedRoute, Router } from '@angular/router';
import { StormInfo, StormShift } from '../../../../../shared/models/storm.model';
import { ConfigurationModel } from '../../../../../shared/models/configuration.model';
import { DistancePipe } from '../../../../../shared/formatting/distance.pipe';
import { WeightPipe } from '../../../../../shared/formatting/weight.pipe';
import { VolumePipe } from '../../../../../shared/formatting/volume.pipe';
import { Observation } from '../../../../../shared/models/observation';
import { MapStyles } from '../../../../../configuration/map-styles';
import { StormMapDataService } from '../storm-map-data.service';
import { HighlightedItemSource } from '../../../../../shared/models/highlighted-item-update';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { Subscription } from 'rxjs';

export class StormStats {
  distanceDriven: number;
  distancePlowed: number;
  distanceSpreadGranular: number;
  distanceSpreadLiquid: number;
  distanceMowed: number;
  distanceSwept: number;
  materialUsedGranular: number;
  materialUsedLiquid: number;
}

@Component({
  selector: 'app-storm-panel',
  imports: [DatePipe, ActionMenuItemComponent, DistancePipe, WeightPipe, VolumePipe, MatIcon, MatIconButton],
  templateUrl: './storm-panel.component.html',
  styleUrl: './storm-panel.component.scss',
  providers: [DatePipe],
})
export class StormPanelComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private datePipe = inject(DatePipe);
  private stormMapDataService = inject(StormMapDataService);

  @Input() configuration: ConfigurationModel;
  @Input() storm: StormInfo;
  @Input() shifts: StormShift[];
  @Input() observations: Observation[];

  shiftsCollapsed = false;
  observationsCollapsed = false;

  shiftMenuItems: ActionMenuItem[];
  observationMenuItems: ActionMenuItem[];
  stormStats: StormStats;

  highlightedShiftId: number;
  highlightedObservationId: number;
  timeTo: number;

  private readonly subscriptions: Subscription[] = [];

  ngOnInit() {
    this.shiftMenuItems = this.shifts.map(shift => {
      return new ActionMenuItem(
        shift.id,
        'task',
        `Shift ${shift.id}`,
        this.datePipe.transform(shift.start * 1000, 'shortTime'),
        '',
        null,
        null,
        () => shift.id === this.highlightedShiftId,
        null,
        null,
        () => this.router.navigate([{ outlets: { shift: [shift.id] } }], { relativeTo: this.activatedRoute }),
        () => this.onHoverShift(shift.id),
        () => this.onHoverShift(null),
        []
      );
    });

    this.updateObservationItems();

    this.subscriptions.push(
      this.stormMapDataService.highlightedShift$.subscribe(update => {
        if (update.source === HighlightedItemSource.MAP) {
          this.highlightedShiftId = update.itemId;
          if (!!this.observations) {
            this.updateObservationItems();
          }
        }
      })
    );

    this.subscriptions.push(
      this.stormMapDataService.highlightedObservation$.subscribe(highlightedObservationUpdate => {
        this.highlightedObservationId = highlightedObservationUpdate.itemId;
        if (!!this.observations) {
          this.updateObservationItems();
        }
      })
    );

    this.subscriptions.push(
      this.stormMapDataService.timeFilter$.subscribe(timeFilter => {
        this.timeTo = timeFilter;
        if (!!this.observations) {
          this.updateObservationItems();
        }
      })
    );

    this.stormStats = this.shifts.reduce(
      (totals, shift) => {
        totals.distanceDriven += shift.distanceDriven ?? 0;
        totals.distancePlowed += shift.distancePlowed ?? 0;
        totals.distanceSpreadGranular += shift.distanceSpreadGranular ?? 0;
        totals.distanceSpreadLiquid += shift.distanceSpreadLiquid ?? 0;
        totals.distanceMowed += shift.distanceMowed ?? 0;
        totals.distanceSwept += shift.distanceSwept ?? 0;
        totals.materialUsedGranular += shift.materialUsedGranular ?? 0;
        totals.materialUsedLiquid += shift.materialUsedLiquid ?? 0;
        return totals;
      },
      {
        distanceDriven: 0,
        distancePlowed: 0,
        distanceSpreadGranular: 0,
        distanceSpreadLiquid: 0,
        distanceMowed: 0,
        distanceSwept: 0,
        materialUsedGranular: 0,
        materialUsedLiquid: 0,
      } as StormStats
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }

  private updateObservationItems() {
    this.observationMenuItems = this.observations
      .map(observation => {
        return new ActionMenuItem(
          observation.id,
          'stars',
          observation.observationType.title,
          this.datePipe.transform(observation.created, 'shortTime'),
          '',
          null,
          !!observation.observationType.group?.mapColor ? observation.observationType.group.mapColor : MapStyles.LIVE_COLOR,
          () => this.highlightedObservationId === observation.id,
          null,
          null,
          () => this.stormMapDataService.sendHighlightedObservationUpdate(observation.id, HighlightedItemSource.PANEL),
          null,
          null,
          []
        );
      });
  }

  toggleShifts() {
    this.shiftsCollapsed = !this.shiftsCollapsed;
  }

  toggleObservations() {
    this.observationsCollapsed = !this.observationsCollapsed;
  }

  onHoverShift(shiftId: number) {
    this.stormMapDataService.sendHighlightedShiftUpdate(shiftId, HighlightedItemSource.PANEL);
  }
}
